import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, Key, FileWarning, Settings, Bell, Clock, ArrowLeft, Check, Globe, Server, Database, Cloud, HashIcon } from 'lucide-react';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { Notification, NotificationsApi } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import { formatTimestamp } from '@/utils/dateTime';

/**
 * Represents a single change detected in an asset
 */
interface AssetChange {
  id: string;
  assetId: string;
  assetName: string;
  assetType: 'domain' | 'subdomain' | 'ip' | 'service' | 'cloud_resource';
  changeType: 'new' | 'modified' | 'deleted' | 'exposed';
  description: string;
  timestamp: string;
  details: {
    [key: string]: string | number | boolean | string[] | number[] | boolean[];
  };
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Extended Notification interface to include summary data
 */
interface ExtendedNotification extends Notification {
  summary?: {
    total: number;
    byType: {
      new: number;
      modified: number;
      deleted: number;
      exposed: number;
    };
    byAssetType: {
      domain: number;
      subdomain: number;
      ip: number;
      service: number;
      cloud_resource: number;
    };
  };
  changes?: AssetChange[];
}

/**
 * Represents a detailed notification about asset changes
 * This can include new assets discovered, modifications to existing assets,
 * or security exposure of assets
 */
interface AssetNotificationDetail {
  id: string;
  type: 'asset_discovery' | 'asset_change' | 'asset_exposure';
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
  changes: AssetChange[];
  summary?: {
    total: number;
    byType: {
      new: number;
      modified: number;
      deleted: number;
      exposed: number;
    };
    byAssetType: {
      domain: number;
      subdomain: number;
      ip: number;
      service: number;
      cloud_resource: number;
    };
  };
}

/**
 * Component for displaying detailed information about asset-related notifications
 * Handles the display of asset changes, their types, risk levels, and detailed attributes
 */
const AssetNotificationDetail: React.FC = () => {
  const { id } = useParams();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [notification, setNotification] = useState<ExtendedNotification | null>(null);

  // Memoize the API-fetching function with useCallback
  const getNotification = useCallback(async () => {
    try {
      // Validate required parameters before API call
      if (!id || !currentTeam?.slug) {
        console.error("Missing required parameters: id or teamSlug");
        return;
      }
      
      const response = await client.notificationsNotificationsRetrieve({ id, teamSlug: currentTeam.slug });
      
      // Add mock summary and changes data
      const extendedNotification: ExtendedNotification = {
        ...response,
        summary: {
          total: 3,
          byType: {
            new: 1,
            modified: 1,
            deleted: 0,
            exposed: 1
          },
          byAssetType: {
            domain: 0,
            subdomain: 1,
            ip: 0,
            service: 1,
            cloud_resource: 1
          }
        },
        changes: [
          {
            id: 'c1',
            assetId: 'a1',
            assetName: 'api-test.example.com',
            assetType: 'subdomain',
            changeType: 'new',
            description: 'New subdomain discovered',
            timestamp: '2024-02-19T17:20:00',
            details: {
              ip_address: '192.168.1.100',
              http_status: 200,
              technologies: ['nginx/1.20.1', 'python/3.9'],
              has_ssl: true
            },
            riskLevel: 'medium'
          },
          {
            id: 'c2',
            assetId: 'a2',
            assetName: 'staging-db.example.com',
            assetType: 'service',
            changeType: 'exposed',
            description: 'Database port exposed to internet',
            timestamp: '2024-02-19T17:21:00',
            details: {
              port: 5432,
              service: 'PostgreSQL',
              version: '14.1',
              previously_exposed: false
            },
            riskLevel: 'critical'
          },
          {
            id: 'c3',
            assetId: 'a3',
            assetName: 'storage-bucket-backup',
            assetType: 'cloud_resource',
            changeType: 'modified',
            description: 'Storage bucket permissions changed',
            timestamp: '2024-02-19T17:22:00',
            details: {
              provider: 'AWS',
              resource_type: 'S3',
              change: 'public_access_block_removed',
              previous_state: 'private'
            },
            riskLevel: 'high'
          }
        ]
      };

      setNotification(extendedNotification);
    } catch (error) {
      console.error("Error fetching notification details:", error);
    }
  }, [id, currentTeam?.slug]);

  useEffect(() => {
    getNotification();
  }, [getNotification]);

  /**
   * Returns the appropriate icon component based on the asset type
   * @param type - The type of asset (domain, subdomain, service, etc.)
   */
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'domain':
      case 'subdomain':
      case 'Ip_Address':
      case 'Ports':
        return Globe;
      case "Publicleak": return Key;
      case "Stealerleak": return Key;
      case "Dataleak": return HashIcon;
      default:
        return Database;
    }
  };
  

  /**
   * Returns the appropriate color class based on the change type
   * @param type - The type of change (new, modified, deleted, exposed)
   */
  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'new': return 'text-blue-500 bg-blue-500/10';
      case 'modified': return 'text-yellow-500 bg-yellow-500/10';
      case 'deleted': return 'text-red-500 bg-red-500/10';
      case 'exposed': return 'text-orange-500 bg-orange-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  /**
   * Returns the appropriate color class based on the risk level
   * @param level - The risk level (critical, high, medium, low)
   */
  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  if (!notification) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Link to="/notifications" className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Notifications
      </Link>

      <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 p-6">
        <div className="flex items-start space-x-4 mb-6">
          <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
            <Bell className="h-6 w-6" />
          </div>
          
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-white mb-2">
              {notification.message}
            </h1>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {notification.timestamp && formatTimestamp(notification.timestamp)}
              </div>
              {notification.read && (
                <div className="flex items-center text-emerald-500">
                  <Check className="h-4 w-4 mr-1" />
                  Read
                </div>
              )}
            </div>
          </div>
        </div>

        {notification.summary && (
          <div className="bg-gray-800/30 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-medium text-white mb-4">Summary</h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="text-2xl font-semibold text-white">{notification.summary?.total}</div>
                <div className="text-sm text-gray-400">Total Changes</div>
              </div>
              <div className="bg-blue-500/10 rounded-lg p-4">
                <div className="text-2xl font-semibold text-blue-500">{notification.summary?.byType.new}</div>
                <div className="text-sm text-gray-400">New Assets</div>
              </div>
              <div className="bg-yellow-500/10 rounded-lg p-4">
                <div className="text-2xl font-semibold text-yellow-500">{notification.summary?.byType.modified}</div>
                <div className="text-sm text-gray-400">Modified</div>
              </div>
              <div className="bg-orange-500/10 rounded-lg p-4">
                <div className="text-2xl font-semibold text-orange-500">{notification.summary?.byType.exposed}</div>
                <div className="text-sm text-gray-400">Exposed</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-6">
              {Object.entries(notification.summary?.byAssetType || {}).map(([type, count]) => {
                if (count === 0) return null;
                const Icon = getAssetIcon(type);
                return (
                  <div key={type} className="flex items-center space-x-2 px-3 py-1.5 bg-gray-800/50 rounded-full">
                    <Icon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-300 capitalize">{type.replace('_', ' ')}</span>
                    <span className="text-sm text-gray-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {notification.changes?.map((change) => {
            const Icon = getAssetIcon(change.assetType);
            return (
              <div key={change.id} className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 rounded-lg bg-gray-700/50">
                      <Icon className="h-4 w-4 text-gray-300" />
                    </div>
                    <div>
                      <h3 className="text-md font-medium text-white">{change.assetName}</h3>
                      <p className="text-sm text-gray-400 mt-1">{change.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(change.riskLevel)}`}>
                      {change.riskLevel.toUpperCase()}
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getChangeTypeColor(change.changeType)}`}>
                      {change.changeType.toUpperCase()}
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-gray-900/50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(change.details).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="text-gray-500 font-medium capitalize">{key.replace('_', ' ')}:</span>
                        <span className="ml-1 text-gray-300">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value.toString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssetNotificationDetail;
